import { useState } from "react";
import { Link } from "react-router";
import { useApi, useFetcher, type Page, type Product, type Project, type ProjectStatus, type Repository } from "../api.js";
import { useOrg } from "../components/Layout.js";
import { Button, Card, Empty, ErrorBox, Loading, PageHeader, Sha, StatusBadge, Time, useAction } from "../components/ui.js";

function ProjectStatusCard({ project }: { project: Project }) {
  const org = useOrg();
  const status = useApi<ProjectStatus>(`/v1/orgs/${org}/projects/${project.slug}/status`, { refreshMs: 15_000 });
  return (
    <Card title={<Link to={`/o/${org}/projects/${project.slug}`}>{project.slug}</Link>} actions={<span className="muted">{project.repository}</span>}>
      <ErrorBox error={status.error} />
      {status.loading && <Loading />}
      {status.data && status.data.environments.length === 0 && <p className="muted">No environments yet.</p>}
      <ul className="env-list">
        {status.data?.environments.map((env) => (
          <li key={env.name}>
            <span className="env-name">
              {env.name}
              {env.protected && <span className="tag">protected</span>}
            </span>
            {env.latest ? (
              <Link to={`/o/${org}/deployments/${env.latest.id}`} className="env-latest">
                <StatusBadge status={env.latest.status} /> <Sha sha={env.latest.commitSha} /> <Time iso={env.latest.updatedAt} />
              </Link>
            ) : (
              <span className="muted">never deployed</span>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function Dashboard() {
  const org = useOrg();
  const projects = useApi<Page<Project>>(`/v1/orgs/${org}/projects`);
  return (
    <>
      <PageHeader title="Dashboard" subtitle="Latest deployment of every environment" />
      <ErrorBox error={projects.error} />
      {projects.loading && <Loading />}
      {projects.data?.items.length === 0 && (
        <Empty title="No projects yet">
          <Link to={`/o/${org}/projects`}>Enrol a repository</Link> to start building and deploying.
        </Empty>
      )}
      <div className="grid">
        {projects.data?.items.map((p) => (
          <ProjectStatusCard key={p.id} project={p} />
        ))}
      </div>
    </>
  );
}

export function Projects() {
  const org = useOrg();
  const projects = useApi<Page<Project>>(`/v1/orgs/${org}/projects`);
  const [enrolling, setEnrolling] = useState(false);
  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="Repositories enrolled in Forge"
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setEnrolling(true);
            }}
          >
            Enrol repository
          </Button>
        }
      />
      {enrolling && (
        <EnrolProject
          onDone={() => {
            setEnrolling(false);
            projects.reload();
          }}
        />
      )}
      <ErrorBox error={projects.error} />
      {projects.loading && <Loading />}
      {projects.data && projects.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Product</th>
              <th>Repository</th>
              <th>Latest repu.yml</th>
            </tr>
          </thead>
          <tbody>
            {projects.data.items.map((p) => (
              <tr key={p.id}>
                <td>
                  <Link to={`/o/${org}/projects/${p.slug}`}>{p.slug}</Link>
                </td>
                <td>{p.product}</td>
                <td>{p.repository}</td>
                <td>{p.latestConfig ? <><Sha sha={p.latestConfig.commitSha} /> <Time iso={p.latestConfig.at} /></> : <span className="muted">not recorded</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function EnrolProject({ onDone }: { onDone: () => void }) {
  const org = useOrg();
  const fetcher = useFetcher();
  const products = useApi<Page<Product>>(`/v1/orgs/${org}/products`);
  const repositories = useApi<Page<Repository>>(`/v1/orgs/${org}/repositories`);
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [product, setProduct] = useState("");
  const [newProduct, setNewProduct] = useState("");
  const [repository, setRepository] = useState("");
  const [defaultBranch, setDefaultBranch] = useState("main");
  const known = repositories.data?.items.find((r) => r.fullName.toLowerCase() === repository.trim().toLowerCase());
  const noProducts = products.data?.items.length === 0;
  const productChoice = noProducts ? "__new" : product;

  const submit = useAction(async () => {
    const fullName = repository.trim();
    // Repositories the GitHub App has not mirrored are registered by hand.
    if (!known) {
      await fetcher(`/v1/orgs/${org}/repositories`, { method: "POST", body: { fullName, defaultBranch: defaultBranch.trim() || "main" } });
    }
    let productSlug = productChoice;
    if (productChoice === "__new") {
      productSlug = newProduct;
      await fetcher(`/v1/orgs/${org}/products`, { method: "POST", body: { slug: newProduct, name: newProduct } });
    }
    await fetcher(`/v1/orgs/${org}/projects`, { method: "POST", body: { slug, product: productSlug, repository: known?.fullName ?? fullName } });
  }, onDone);

  const onRepository = (value: string) => {
    setRepository(value);
    const name = value.split("/")[1]?.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "") ?? "";
    if (!slugEdited) setSlug(name);
    if (!newProduct) setNewProduct(name);
  };

  return (
    <Card title="Enrol a repository">
      <form
        className="form inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit.run();
        }}
      >
        <label>
          GitHub repository
          <input
            list="known-repositories"
            value={repository}
            onChange={(e) => { onRepository(e.target.value); }}
            required
            pattern="[A-Za-z0-9_.\-]+/[A-Za-z0-9_.\-]+"
            placeholder="REPU-ME/onyx"
            autoComplete="off"
            spellCheck={false}
          />
          <datalist id="known-repositories">
            {repositories.data?.items.map((r) => (
              <option key={r.id} value={r.fullName} />
            ))}
          </datalist>
        </label>
        {repository.trim() !== "" && !known && (
          <label>
            Default branch
            <input value={defaultBranch} onChange={(e) => { setDefaultBranch(e.target.value); }} required />
          </label>
        )}
        {!noProducts && (
          <label>
            Product
            <select value={product} onChange={(e) => { setProduct(e.target.value); }} required>
              <option value="">Choose…</option>
              {products.data?.items.map((p) => (
                <option key={p.id} value={p.slug}>
                  {p.name}
                </option>
              ))}
              <option value="__new">New product…</option>
            </select>
          </label>
        )}
        {productChoice === "__new" && (
          <label>
            {noProducts ? "Product" : "New product slug"}
            <input value={newProduct} onChange={(e) => { setNewProduct(e.target.value); }} required pattern="[a-z0-9]([a-z0-9\-]*[a-z0-9])?" placeholder="onyx-peptides" />
          </label>
        )}
        <label>
          Project slug
          <input value={slug} onChange={(e) => { setSlug(e.target.value); setSlugEdited(true); }} required pattern="[a-z0-9]([a-z0-9\-]*[a-z0-9])?" />
        </label>
        <ErrorBox error={submit.error} />
        <div className="form-actions">
          <Button type="button" onClick={onDone}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" busy={submit.busy}>
            Enrol
          </Button>
        </div>
        <p className="muted small wide">
          {repositories.data?.items.length === 0
            ? "Type the repository as owner/name. Linking the GitHub App in Settings mirrors repositories automatically and lets pushes trigger pipelines."
            : "Pick a mirrored repository or type owner/name to register one by hand."}{" "}
          The product must match <code>product:</code> in the repository's repu.yml.
        </p>
      </form>
    </Card>
  );
}
